import settings from '/@/core/settings';
import { _ } from '/@/locale';

const weightUnits = {
    metric: 'kg',
    imperial: 'lb',
};

const speedUnits = {
    metric: 'km/h',
    imperial: 'mph',
};

const temperatureUnits = {
    metric: '°C',
    imperial: '°F',
};

const rainUnits = {
    metric: 'mm',
    imperial: 'in',
};

const systemRatios = {
    metric: 1, // kg (default)
    imperial: 2.20462, // lb
};

const rainRatios = {
    metric: 1, // mm (default)
    imperial: 0.0393701, // in
};

const temperatureRatios = {
    metric: 1, // C (default)
    imperial: 9 / 5, // F conversion ratio (9/5 = 1.8)
};

const temperatureOffsets = {
    metric: 0, // No offset for Celsius
    imperial: 32, // Offset for Fahrenheit
};

const speedRatios = {
    metric: 1, // km/h (default)
    imperial: 0.621371, // mph
};

const areaRatios = {
    ha: 1, // default
    m2: 10000,
    ac: 2.47105,
    alq: 1 / 2.42, // alqueire paulista
};

const yieldRatios = {
    kg: 1, // default
    lb: 2.20462,
    ton: 1 / 1000, // actually 'tonne'
    qq: 1 / 100,
    sacas: 1 / 60,
    bu: {
        corn: 1 / 25.4,
        soy: 1 / 27.2155,
        wheat: 1 / 27.2155, // wheat bushel = 60 lb
        barley: 1 / 21.7724, // barley bushel = 48 lb
    },
};

const defaultCropSpacing = {
    soy: 0.5,
    corn: 0.52,
};

const winterCerealCrops = ['wheat', 'barley'];

const getDefaultCrop = (crop: string) => {
    const crops = ['corn', 'soy', ...winterCerealCrops];
    return crops.find((c) => c === crop) || 'corn';
};

// Wheat and barley ("cereales de invierno") share the same unit preferences.
const getUnitCrop = (crop: string) => (winterCerealCrops.includes(crop) ? 'winterCereal' : crop);

export class UnitConverter {
    constructor({
        system,
        area,
        cornYield,
        cornDensity,
        soyYield,
        soyDensity,
        winterCerealYield,
        winterCerealDensity,
        language,
        brands,
        speed,
        temperature,
        rain,
    }) {
        this.system = system;
        this.area = area;
        this.cornYield = cornYield;
        this.cornDensity = cornDensity;
        this.soyYield = soyYield;
        this.soyDensity = soyDensity;
        this.winterCerealYield = winterCerealYield;
        this.winterCerealDensity = winterCerealDensity;
        this.language = language;
        this.brands = brands;
        this.speed = speed;
        this.temperature = temperature;
        this.rain = rain;
    }

    clone(overrides) {
        return new UnitConverter({
            system: this.system,
            area: this.area,
            cornYield: this.cornYield,
            cornDensity: this.cornDensity,
            soyYield: this.soyYield,
            soyDensity: this.soyDensity,
            winterCerealYield: this.winterCerealYield,
            winterCerealDensity: this.winterCerealDensity,
            language: this.language,
            brands: this.brands,
            speed: this.speed,
            temperature: this.temperature,
            rain: this.rain,
            ...overrides,
        });
    }

    getUnit(type, crop, stage = 'harvest') {
        crop = getDefaultCrop(crop);
        if (type === 'area') return this.area;
        if (type === 'weight') return weightUnits[this.system];
        if (type === 'weight/area') return `${weightUnits[this.system]}/${this.area}`;
        if (type === 'speed') return speedUnits[this.system];
        if (type === 'rain') return rainUnits[this.system];
        if (type === 'temperature') return temperatureUnits[this.system];

        const unitCrop = getUnitCrop(crop);
        const key = `${unitCrop}${type?.[0]?.toUpperCase()}${type.substr(1)}`;
        if (!this[key]) {
            // Fallback while the unit preference for this crop hasn't been set yet
            if (type === 'yield') return `${weightUnits[this.system]}/${this.area}`;
            throw new Error(`UnitConverter.getUnit: Invalid type: "${type}" or crop: "${crop}".`);
        }

        if (type === 'yield') return `${this[key]}/${this.area}`;

        if (type === 'density') {
            const unit = settings.enums[`${unitCrop}DensityUnits`][this[key]].label.toLowerCase();
            if (stage === 'harvest') return unit;
            if (stage === 'planting') return `${_('semillas')}/${unit.split('/')[1]}`;
            // throw new Error(`UnitConverter.getUnit: Invalid stage: "${stage}".`);
        }
    }

    toArea(value, precision) {
        const result = value * areaRatios[this.area];
        const unit = this.getUnit('area');
        return this.getReturnValue(result, unit, precision);
    }

    fromArea(value) {
        return value / areaRatios[this.area];
    }

    toWeight(value, precision) {
        const result = value * systemRatios[this.system];
        const unit = this.getUnit('weight');
        return this.getReturnValue(result, unit, precision);
    }

    fromWeight(value) {
        return value / systemRatios[this.system];
    }

    toSpeed(value, precision) {
        const result = value * speedRatios[this.system];
        const unit = this.getUnit('speed');
        return this.getReturnValue(result, unit, precision);
    }

    fromSpeed(value) {
        return value / speedRatios[this.system];
    }

    toWeightPerArea(value, precision) {
        const result = (value * systemRatios[this.system]) / areaRatios[this.area];
        const unit = this.getUnit('weight/area');
        return this.getReturnValue(result, unit, precision);
    }

    fromWeightPerArea(value) {
        return (value / systemRatios[this.system]) * areaRatios[this.area];
    }

    toYield(value, crop, precision) {
        crop = getDefaultCrop(crop);
        const yieldUnit = this[`${getUnitCrop(crop)}Yield`];
        if (!yieldUnit) {
            const result = value / areaRatios[this.area];
            const unit = `${weightUnits[this.system]}/${this.area}`;
            return this.getReturnValue(result, unit, precision);
        }

        let yieldRatio = yieldRatios[yieldUnit];
        if (typeof yieldRatio === 'object') yieldRatio = yieldRatio[crop];

        const areaRatio = areaRatios[this.area];

        const result = (value * yieldRatio) / areaRatio;
        const unit = this.getUnit('yield', crop);

        return this.getReturnValue(result, unit, precision);
    }

    toCornYield(value, precision) {
        return this.toYield(value, 'corn', precision);
    }

    toSoyYield(value, precision) {
        return this.toYield(value, 'soy', precision);
    }

    fromYield(value, crop) {
        crop = getDefaultCrop(crop);
        const yieldUnit = this[`${getUnitCrop(crop)}Yield`];
        if (!yieldUnit) throw new Error(`UnitConverter.fromYield: Invalid crop: ${crop}.`);

        let yieldRatio = yieldRatios[yieldUnit];
        if (typeof yieldRatio === 'object') yieldRatio = yieldRatio[crop];
        const areaRatio = areaRatios[this.area];
        return (value * areaRatio) / yieldRatio;
    }

    toDensity(value, crop, precision, stage = 'harvest') {
        crop = getDefaultCrop(crop);
        const densityUnit = this[`${getUnitCrop(crop)}Density`];
        if (!densityUnit) throw new Error(`UnitConverter.toDensity: Invalid crop: "${crop}".`);

        const unit = this.getUnit('density', crop, stage);

        // Support multiple recommended density values (for soy)...
        // TODO: remove this after SoyDensity.value is changed to number field.
        if (typeof value === 'string' && value.includes('-')) {
            const [value1, value2] = value.split('-');
            const result1 = this.toDensity(Number(value1), crop, precision);
            const result2 = this.toDensity(Number(value2), crop, precision);
            const result = `${result1.localeValue} - ${result2.localeValue}`;
            return {
                value: result,
                localeValue: result,
                unit,
                toString: () => `${result} ${unit}`,
            };
        }

        const cropSpacing = this.brands?.[crop]?.cropSpacing || defaultCropSpacing[crop]; //TODO: BRAND
        const result =
            densityUnit === 'm'
                ? (value * defaultCropSpacing[crop]) / cropSpacing / (10000 / cropSpacing)
                : value / areaRatios[densityUnit];

        return this.getReturnValue(result, unit, precision);
    }

    toCornDensity(value, precision, stage = 'harvest') {
        return this.toDensity(value, 'corn', precision, stage);
    }

    toSoyDensity(value, precision, stage = 'harvest') {
        return this.toDensity(value, 'soy', precision, stage);
    }

    fromDensity(value, crop) {
        crop = getDefaultCrop(crop);
        const densityUnit = this[`${getUnitCrop(crop)}Density`];
        if (!densityUnit) throw new Error(`UnitConverter.fromDensity: Invalid crop: "${crop}".`);
        if (densityUnit === 'm') {
            const cropSpacing = this.brands?.[crop]?.cropSpacing || defaultCropSpacing[crop]; //TODO: BRAND
            return value * (10000 / cropSpacing);
        }

        return value * areaRatios[densityUnit];
    }

    fromCornDensity(value) {
        return this.fromDensity(value, 'corn');
    }

    fromSoyDensity(value) {
        return this.fromDensity(value, 'soy');
    }

    toBags(value, precision) {
        const result = value / areaRatios[this.area];
        const unit = `${_('bolsas')}/${this.getUnit('area')}`;
        return this.getReturnValue(result, unit, precision);
    }

    toBagsConverted(value, precision) {
        const result = value;
        const unit = `${_('bolsas')}/${this.getUnit('area')}`;
        return this.getReturnValue(result, unit, precision);
    }

    toPercentage(value, precision = 0) {
        // TODO: tidy
        if (value < 10) precision = 1;
        return this.getReturnValue(String(Number(value.toFixed(precision))), '%');
    }

    toLocaleValue(value) {
        return value.toLocaleString(this.language);
    }

    parsePrecision(value, unit, precision) {
        if (unit.includes('ton')) return 1;
        if (precision === undefined) return value < 1000 ? 1 : 0;
        return precision;
    }

    getReturnValue(value, unit, precision) {
        let localeValue = value;
        if (typeof value === 'number') {
            value = Number(value.toFixed(this.parsePrecision(value, unit, precision)));
            if (value === 0) value = 0; // Avoid returning "-0.00" due to precission
            localeValue = this.toLocaleValue(value);
        }
        return { value, localeValue, unit, toString: () => `${localeValue} ${unit}` };
    }

    convertYieldToKilograms(value, crop, precision) {
        crop = getDefaultCrop(crop);
        const yieldUnit = this[`${getUnitCrop(crop)}Yield`];
        if (!yieldUnit) throw new Error(`UnitConverter.fromYield: Invalid crop: ${crop}.`);

        let yieldRatio = yieldRatios[yieldUnit];
        if (typeof yieldRatio === 'object') {
            if (!(crop in yieldRatio)) {
                throw new Error(`UnitConverter.convertYieldToKilograms: Invalid crop: "${crop}".`);
            }
            yieldRatio = yieldRatio[crop];
        }

        return Number((value / yieldRatio).toFixed(precision));
    }

    toTemperature(value, precision) {
        const unit = this.getUnit('temperature');
        const converted = value * temperatureRatios[this.system] + temperatureOffsets[this.system];
        const result = Number(converted.toFixed(precision));
        return this.getReturnValue(result, unit, precision);
    }

    fromTemperature(value, precision) {
        const converted = (value - temperatureOffsets[this.system]) / temperatureRatios[this.system];
        return Number(converted.toFixed(precision));
    }

    toRain(value, precision) {
        const result = value * rainRatios[this.system];
        const unit = this.getUnit('rain');
        return this.getReturnValue(result, unit, precision);
    }
}
